import type { GroupCreateRequest } from '@quromedical/models';
import { Alert, Skeleton, SkeletonProvider, Snackbar } from 'design-system';
import { router } from 'expo-router';
import { useFiniteState } from 'hooks/useFiniteState';
import { useRevalidation } from 'hooks/useRevalidation';
import { organizationFetcher } from 'integration';
import { GroupApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback } from 'react';
import { strings } from 'strings';

import { Form } from '../forms';

type FormState = 'editing' | 'submitting' | 'error';
const api = new GroupApi();

const orgFetcher = organizationFetcher();

export const CreateGroupCard: React.FC = () => {
  const state = useFiniteState<FormState>('editing');
  const revalidate = useRevalidation('Group');

  const onSubmit = useCallback(
    async (data: GroupCreateRequest) => {
      state.set('submitting');
      try {
        const result = await api.createGroup(data);
        state.set('editing');
        router.push({ pathname: `/admin/group/${result.id}` });
        revalidate();
        return {};
      } catch (error) {
        state.set('error');
        return { error };
      }
    },
    [revalidate, state],
  );

  return (
    <>
      <SkeletonProvider loading={state.is('submitting')}>
        <Skeleton>
          <Form
            data={{}}
            handleSubmit={onSubmit}
            organizationFetcher={orgFetcher}
          />
        </Skeleton>
      </SkeletonProvider>

      <Snackbar onClose={state.next('editing')} isOpen={state.is('error')}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={state.next('editing')}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    </>
  );
};
